import classnames from 'classnames';
import React, { ReactNode, useState } from 'react';

import '@eika/attention-message-style';
import { SmallButton } from '@eika/button';
import { useViewType, ViewType } from '@eika/hooks';
import { Icon, IconTypes } from '@eika/icon';
import { Link } from '@eika/link';

import AttentionMessageTypes from './AttentionMessageTypes';

export interface AttentionMessageProps {
    id?: string;
    type: AttentionMessageTypes;
    text: ReactNode;
    header?: string;
    moreText?: string;
    icon?: IconTypes;
    className?: string;
    clickableItem?: ReturnType<typeof Link> | ReturnType<typeof SmallButton>;
    inlineClickableItem?: boolean;
}

type ToggleMoreProps = {
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    children: React.ReactNode;
};

const ToggleMore = ({ onClick, children }: ToggleMoreProps): JSX.Element => {
    return (
        <button type="button" onClick={onClick} className="expand-button">
            {children}
        </button>
    );
};

const AttentionMessage = ({
    id,
    text,
    header,
    moreText,
    icon,
    type,
    clickableItem,
    className,
    inlineClickableItem = false,
    ...rest
}: AttentionMessageProps): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState(false);
    const viewType = useViewType();
    const isSmallDevice = viewType === ViewType.mobile;

    const toggleHiddenContent = ({ preventDefault }: React.SyntheticEvent) => {
        setIsExpanded(!isExpanded);
        preventDefault();
    };

    return (
        <div id={id} className={classnames('sgw-attention-message', type, className)} {...rest}>
            <div className={`sgw-attention-message__inner ${moreText ? 'sgw-attention-message__expandable' : ''}`}>
                {icon && <Icon wrapperClassName="sgw-attention-message__icon" type={icon} />}
                <div className="message-text__container">
                    {header && <h2 className="sgw-attention-message__header">{header}</h2>}
                    <p
                        className={classnames(
                            'message-text',
                            { 'message-text_clickableItem': clickableItem },
                            { inline_clickableitem: inlineClickableItem },
                        )}
                    >
                        {text}
                        {!moreText && inlineClickableItem && clickableItem && (
                            <span className="clickableItem">{clickableItem}</span>
                        )}
                        {moreText && isSmallDevice && inlineClickableItem && clickableItem && (
                            <span className="clickableItem">{clickableItem}</span>
                        )}
                    </p>
                    {(moreText && !isSmallDevice) ||
                        (!moreText && !inlineClickableItem && clickableItem && <span>{clickableItem}</span>)}
                </div>
                {moreText && !isSmallDevice && (
                    <ToggleMore onClick={toggleHiddenContent}>
                        <Icon wrapperClassName="sgw-attention-message__icon" type={IconTypes.LITEN_PIL_NED} />
                    </ToggleMore>
                )}
            </div>
            {moreText && isExpanded && (
                <div className="sgw-attention-message__more">
                    <p>{moreText}</p>
                    {clickableItem && <span>{clickableItem}</span>}
                </div>
            )}
        </div>
    );
};

export default AttentionMessage;
